import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CheckCircle2,
  ClipboardList,
  History,
  Inbox,
  Pencil,
  UploadCloud,
  UserPlus,
  type Users,
} from 'lucide-react';
import { apiRequest, ApiError, type ListResponse, apiListAll } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { TextField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { AddRelationModal } from '../components/shared/AddRelationModal';
import { EntityPicker, type PickedEntity } from '../components/shared/EntityPicker';
import { StatusTransitionButton } from '../components/shared/StatusTransitionButton';
import { ClaimStatusBadge, FeeStatusBadge } from '../components/finance/StatusBadge';
import { CycleDrawer } from '../components/finance/CycleDrawer';
import { normalisePaymentRow } from '../components/finance/normalisers';
import { formatINR, formatDate, timeAgo } from '../lib/format';
import {
  cycleLabel,
  type FeeCycle,
  type FeeCycleStatus,
  type FeeClaimJoined,
  type FeePaymentJoined,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/fees/$cycleId')({
  component: FeeCycleDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes — match the enriched detail GET documented in §4.13
// ---------------------------------------------------------------------------

interface CreatedByEmbed {
  id: string;
  displayName: string;
  email: string | null;
}

interface CycleKpis {
  records: number;
  totalBilledPaise: number;
  collectedPaise: number;
  outstandingPaise: number;
  defaulters: number;
  pendingClaims: number;
}

interface FeeCycleDetail extends FeeCycle {
  createdBy: string | null;
  createdByUser?: CreatedByEmbed | null;
  kpis?: CycleKpis;
}

interface StudentSummary {
  id: string;
  name: string;
  admissionNumber: string | null;
  householdId: string | null;
}

type Tab = 'payments' | 'missing' | 'claims' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'payments', label: 'Payments', icon: ClipboardList },
  { value: 'missing', label: 'Missing students', icon: UserPlus },
  { value: 'claims', label: 'Reported by parents', icon: Inbox },
  { value: 'activity', label: 'Activity', icon: History },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FeeCycleDetailPage(): JSX.Element {
  const { cycleId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role === 'super_admin' || role === 'finance_admin';
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('payments');
  const [editOpen, setEditOpen] = useState(false);

  const cycleQuery = useQuery({
    queryKey: ['fee-cycle', 'detail', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeeCycleDetail>(`/api/fees/cycles/${encodeURIComponent(cycleId)}`, { signal }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const cycle = cycleQuery.data;

  const invalidateCycle = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['fee-cycle', 'detail', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['feeCycles'] });
  };

  const invalidatePayments = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['fee-cycle', 'payments', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['fee-cycle', 'missing', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['feePayments'] });
    invalidateCycle();
  };

  const topUpMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true; created: number }>(
        `/api/fees/cycles/${encodeURIComponent(cycleId)}/generate-records`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      toast(
        res.created > 0
          ? `Top-up complete — ${res.created} record(s) added.`
          : 'Top-up complete — no new students to add.',
        { variant: 'success' },
      );
      invalidatePayments();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not top up records.', {
        variant: 'error',
      });
    },
  });

  // Bulk mark-all-paid for the cycle's pending payments. We fetch the cycle's
  // pending IDs from the payments query (cached) and reuse the existing bulk
  // endpoint. The bulk schema caps at 100 IDs per request so we chunk the IDs
  // and POST in batches. Keeps this slice consistent with the list page and
  // safe for cycles with many records.
  const BULK_BATCH_SIZE = 100;
  const pendingIdsForCycleQuery = useQuery({
    queryKey: ['fee-cycle', 'pending-ids', cycleId] as const,
    enabled: false,
    queryFn: async ({ signal }) => {
      // The list API returns at most 100 a page — read every page.
      const ids: string[] = [];
      for (let page = 1; ; page += 1) {
        const res = await apiRequest<{ items: unknown[]; total?: number }>(`/api/fees/payments`, {
          signal,
          // Everything unpaid: pending, and overdue once the due date has passed.
          query: { cycleId, status: 'pending,overdue', page, pageSize: BULK_BATCH_SIZE },
        });
        const items = res.items ?? [];
        for (const row of items) {
          const id = normalisePaymentRow(row).payment.id;
          if (id) ids.push(id);
        }
        const seen = (page - 1) * BULK_BATCH_SIZE + items.length;
        if (items.length < BULK_BATCH_SIZE || (typeof res.total === 'number' && seen >= res.total)) break;
      }
      return ids;
    },
  });

  const handleMarkAllPaid = async (): Promise<void> => {
    const res = await pendingIdsForCycleQuery.refetch();
    if (res.isError) {
      toast(res.error instanceof ApiError ? res.error.message : 'Could not load the unpaid fees.', { variant: 'error' });
      return;
    }
    const ids = res.data ?? [];
    if (ids.length === 0) {
      toast('No unpaid fees to mark.', { variant: 'info' });
      return;
    }
    const paidAt = new Date().toISOString();
    try {
      let total = 0;
      for (let i = 0; i < ids.length; i += BULK_BATCH_SIZE) {
        const batch = ids.slice(i, i + BULK_BATCH_SIZE);
        const result = await apiRequest<{ ok: true; count: number }>(
          '/api/fees/payments/bulk-mark-paid',
          {
            method: 'POST',
            body: {
              paymentIds: batch,
              paidAt,
              paidVia: 'cash',
            },
          },
        );
        total += result.count;
      }
      toast(`${total} payments marked as paid.`, { variant: 'success' });
      invalidatePayments();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not mark all paid.', {
        variant: 'error',
      });
    }
  };

  const handleClose = async (): Promise<void> => {
    try {
      await apiRequest<{ ok: true }>(
        `/api/fees/cycles/${encodeURIComponent(cycleId)}/close`,
        { method: 'POST' },
      );
      toast('Cycle closed.', { variant: 'success' });
      invalidateCycle();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not close cycle.', {
        variant: 'error',
      });
    }
  };

  const handleCancel = async (): Promise<void> => {
    try {
      await apiRequest<{ ok: true }>(
        `/api/fees/cycles/${encodeURIComponent(cycleId)}/cancel`,
        { method: 'POST' },
      );
      toast('Cycle cancelled.', { variant: 'success' });
      invalidateCycle();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not cancel cycle.', {
        variant: 'error',
      });
    }
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!cycle) return [];
    const k = cycle.kpis;
    const collectedPct =
      k && k.totalBilledPaise > 0
        ? Math.round((k.collectedPaise / k.totalBilledPaise) * 100)
        : 0;
    return [
      { label: 'Records', value: k ? k.records : '—', onClick: () => setTab('payments') },
      { label: 'Total billed', value: k ? formatINR(k.totalBilledPaise) : '—' },
      {
        label: 'Collected',
        value: k ? formatINR(k.collectedPaise) : '—',
        sub: k ? `${collectedPct}%` : undefined,
      },
      { label: 'Outstanding', value: k ? formatINR(k.outstandingPaise) : '—' },
      {
        label: 'Defaulters',
        value: k ? k.defaulters : '—',
        onClick: () => setTab('missing'),
      },
      {
        label: 'Reported payments to check',
        value: k ? k.pendingClaims : '—',
        onClick: () => setTab('claims'),
      },
    ];
  }, [cycle]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={cycleQuery.error}
        title="Fee cycle"
        onRetry={() => cycleQuery.refetch()}
      >
        {cycleQuery.isLoading || !cycle ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={cycleLabel(cycle.year, cycle.month)}
              sublabel={
                <>
                  <span className="tabular-nums">
                    Due {formatDate(`${cycle.dueDate}T00:00:00Z`)}
                  </span>
                  {cycle.createdByUser ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>Created by {cycle.createdByUser.displayName}</span>
                    </>
                  ) : null}
                </>
              }
              statusChip={<CycleStatusChip status={cycle.status} />}
              backTo="/finance/fees"
              actions={
                isAdmin ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit cycle
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UploadCloud size={14} aria-hidden="true" />}
                      loading={topUpMutation.isPending}
                      onClick={() => topUpMutation.mutate()}
                    >
                      Top up records
                    </Button>
                    <StatusTransitionButton
                      label="Mark all paid"
                      icon={<CheckCircle2 size={14} aria-hidden="true" />}
                      variant="primary"
                      size="sm"
                      disabled={cycle.status !== 'open'}
                      confirmCopy={{
                        title: 'Mark every unpaid fee as paid?',
                        description:
                          'Every unpaid fee in this cycle, including overdue ones, is marked paid in cash with today\'s date.',
                        confirmLabel: 'Mark all paid',
                      }}
                      onConfirm={handleMarkAllPaid}
                    />
                    {cycle.status === 'open' ? (
                      <StatusTransitionButton
                        label="Close"
                        variant="secondary"
                        size="sm"
                        confirmCopy={{
                          title: 'Close this fees cycle?',
                          description:
                            'Closing prevents further edits to payments and finalises the period.',
                          confirmLabel: 'Close cycle',
                        }}
                        onConfirm={handleClose}
                      />
                    ) : null}
                    {isSuperAdmin && cycle.status === 'open' ? (
                      <StatusTransitionButton
                        label="Cancel"
                        variant="danger"
                        size="sm"
                        confirmCopy={{
                          title: 'Cancel this fees cycle?',
                          description:
                            'This is permanent. Cancellation is only allowed when no payments have been collected.',
                          confirmLabel: 'Cancel cycle',
                          variant: 'danger',
                        }}
                        onConfirm={handleCancel}
                      />
                    ) : null}
                  </>
                ) : null
              }
            />

            {/* Cards render '—' for missing values, so we drop loading once
                the cycle itself is loaded. The kpis sub-object may arrive
                in a later backend slice; the strip stays useful either way. */}
            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Cycle sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'payments' ? (
              <PaymentsTab cycleId={cycle.id} />
            ) : null}
            {tab === 'missing' ? (
              <MissingStudentsTab
                cycleId={cycle.id}
                canManage={isAdmin}
                onAdded={invalidatePayments}
              />
            ) : null}
            {tab === 'claims' ? (
              <ClaimsTab cycleId={cycle.id} />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel targetType="fee_cycle" targetId={cycle.id} title="Activity" />
            ) : null}
          </div>
        )}
      </GatedSection>

      {isAdmin && cycle ? (
        <CycleDrawer
          cycle={editOpen ? cycle : null}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            invalidateCycle();
            toast('Cycle saved.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

function CycleStatusChip({ status }: { status: FeeCycleStatus }): JSX.Element {
  const tone =
    status === 'open'
      ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
      : status === 'closed'
        ? 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]'
        : 'border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 text-[color:var(--color-danger)]';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Payments tab
// ---------------------------------------------------------------------------

function PaymentsTab({ cycleId }: { cycleId: string }): JSX.Element {
  const navigate = useNavigate();
  const paymentsQuery = useQuery({
    queryKey: ['fee-cycle', 'payments', cycleId] as const,
    queryFn: async ({ signal }) => {
      // `/api/fees/payments` currently returns raw payment rows; the backend
      // is incrementally moving to the joined shape. Normalise both so this
      // tab renders regardless of which side of the migration we're on.
      // Every page: the API sends at most 100 rows at a time.
      const all = await apiListAll<unknown>('/api/fees/payments', { signal, query: { cycleId } });
      return all.map(normalisePaymentRow);
    },
  });

  const items: FeePaymentJoined[] = paymentsQuery.data ?? [];
  const errorState = paymentsQuery.isError
    ? {
        message:
          paymentsQuery.error instanceof ApiError
            ? paymentsQuery.error.message
            : 'Could not load payments.',
        onRetry: (): void => {
          void paymentsQuery.refetch();
        },
      }
    : null;

  return (
    <RelationPanel
      title="Payments"
      count={paymentsQuery.data ? items.length : undefined}
      description="Every payment record generated for this cycle."
      loading={paymentsQuery.isLoading && !paymentsQuery.data}
      error={errorState}
      isEmpty={!paymentsQuery.isLoading && items.length === 0}
      empty={{ copy: 'No payment records yet. Top up to create them.' }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Student</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-right">Net</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Paid on</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const p = row.payment;
              return (
                <tr
                  key={p.id}
                  onClick={() =>
                    void navigate({
                      to: '/finance/fees/payments/$paymentId',
                      params: { paymentId: p.id },
                    })
                  }
                  className="cursor-pointer border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
                >
                  <td className="px-4 py-2 align-middle">
                    <div className="font-medium text-[color:var(--color-text)]">
                      {row.student.name}
                    </div>
                    {row.student.admissionNumber ? (
                      <div className="text-xs text-[color:var(--color-text-muted)]">
                        {row.student.admissionNumber}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {formatINR(p.amountPaise)}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums font-medium">
                    {formatINR(p.netAmountPaise)}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <FeeStatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {p.paidAt ? formatDate(p.paidAt, { withTime: true }) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Missing students tab — admin can add one student to the cycle
// ---------------------------------------------------------------------------

interface MissingStudentsTabProps {
  cycleId: string;
  canManage: boolean;
  onAdded: () => void;
}

function MissingStudentsTab({
  cycleId,
  canManage,
  onAdded,
}: MissingStudentsTabProps): JSX.Element {
  const { toast } = useToast();
  const [addFor, setAddFor] = useState<StudentSummary | null>(null);
  const [picked, setPicked] = useState<PickedEntity | null>(null);
  const [openPicker, setOpenPicker] = useState(false);
  const [amountRupees, setAmountRupees] = useState('');
  const [discountRupees, setDiscountRupees] = useState('');

  const missingQuery = useQuery({
    queryKey: ['fee-cycle', 'missing', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: StudentSummary[] }>(
        `/api/fees/cycles/${encodeURIComponent(cycleId)}/missing-students`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const items = missingQuery.data?.items ?? [];
  const errorState = missingQuery.isError
    ? {
        message:
          missingQuery.error instanceof ApiError
            ? missingQuery.error.message
            : 'Could not load missing students.',
        onRetry: (): void => {
          void missingQuery.refetch();
        },
      }
    : null;

  const submit = async (): Promise<{ id: string }> => {
    const targetId = addFor?.id ?? picked?.id;
    if (!targetId) {
      throw new ApiError(400, 'invalid', 'Pick a student to add.');
    }
    const amountPaise = Math.round(Number(amountRupees) * 100);
    if (!Number.isFinite(amountPaise) || amountPaise <= 0) {
      throw new ApiError(400, 'invalid', 'Enter a valid amount.');
    }
    const discountPaiseRaw = Math.round(Number(discountRupees || '0') * 100);
    const discountPaise = Number.isFinite(discountPaiseRaw) ? discountPaiseRaw : 0;
    const body: Record<string, unknown> = {
      studentId: targetId,
      amountPaise,
    };
    if (discountPaise > 0) body.discountPaise = discountPaise;
    return apiRequest<{ id: string }>(
      `/api/fees/cycles/${encodeURIComponent(cycleId)}/payments`,
      { method: 'POST', body },
    );
  };

  const closeModal = (): void => {
    setAddFor(null);
    setOpenPicker(false);
    setPicked(null);
    setAmountRupees('');
    setDiscountRupees('');
  };

  const isOpen = addFor !== null || openPicker;
  const modalTitle = addFor ? `Add ${addFor.name} to cycle` : 'Add a student to cycle';

  return (
    <>
      <RelationPanel
        title="Missing students"
        count={missingQuery.data ? items.length : undefined}
        description="Active students who do not yet have a payment record in this cycle."
        action={
          canManage ? (
            <Button
              size="sm"
              icon={<UserPlus size={14} aria-hidden="true" />}
              onClick={() => {
                setPicked(null);
                setAddFor(null);
                setAmountRupees('');
                setDiscountRupees('');
                setOpenPicker(true);
              }}
            >
              Add a student
            </Button>
          ) : null
        }
        loading={missingQuery.isLoading && !missingQuery.data}
        error={errorState}
        isEmpty={!missingQuery.isLoading && items.length === 0}
        empty={{ copy: 'Every active student has a record in this cycle.' }}
      >
        <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          {items.map((s) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5"
            >
              <div className="min-w-0">
                <div className="font-medium text-[color:var(--color-text)] truncate">
                  {s.name}
                </div>
                {s.admissionNumber ? (
                  <div className="text-xs text-[color:var(--color-text-muted)]">
                    {s.admissionNumber}
                  </div>
                ) : null}
              </div>
              {canManage ? (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<UserPlus size={14} aria-hidden="true" />}
                  onClick={() => {
                    setAddFor(s);
                    setPicked(null);
                    setAmountRupees('');
                    setDiscountRupees('');
                  }}
                >
                  Add to cycle
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </RelationPanel>

      {canManage ? (
        <AddRelationModal<{ id: string }>
          open={isOpen}
          onClose={closeModal}
          title={modalTitle}
          description="Set the gross amount in rupees; the net is computed server-side."
          picker={
            addFor ? (
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm">
                <div className="font-medium">{addFor.name}</div>
                {addFor.admissionNumber ? (
                  <div className="text-xs text-[color:var(--color-text-muted)]">
                    {addFor.admissionNumber}
                  </div>
                ) : null}
              </div>
            ) : (
              <EntityPicker
                entity="student"
                {...(picked ? { value: picked } : {})}
                onChange={(v) => {
                  if (Array.isArray(v)) setPicked(v[0] ?? null);
                  else setPicked(v.id ? v : null);
                }}
              />
            )
          }
          fields={
            <>
              <TextField
                label="Amount (₹)"
                type="number"
                min={0}
                step="0.01"
                value={amountRupees}
                onChange={(e) => setAmountRupees(e.target.value)}
              />
              <TextField
                label="Discount (₹)"
                type="number"
                optional
                min={0}
                step="0.01"
                value={discountRupees}
                onChange={(e) => setDiscountRupees(e.target.value)}
              />
            </>
          }
          submitLabel="Add to cycle"
          canSubmit={
            Boolean((addFor?.id ?? picked?.id) && amountRupees.trim() !== '')
          }
          onSubmit={submit}
          onSuccess={() => {
            toast('Student added to cycle.', { variant: 'success' });
            onAdded();
            closeModal();
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Claims tab — read-only list of this cycle's claims
// ---------------------------------------------------------------------------

function ClaimsTab({ cycleId }: { cycleId: string }): JSX.Element {
  const claimsQuery = useQuery({
    queryKey: ['fee-cycle', 'claims', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse<FeeClaimJoined>>('/api/fees/claims', {
        signal,
        query: { cycleId, pageSize: 100 },
      }),
  });

  const items = claimsQuery.data?.items ?? [];
  const errorState = claimsQuery.isError
    ? {
        message:
          claimsQuery.error instanceof ApiError
            ? claimsQuery.error.message
            : 'Could not load claims.',
        onRetry: (): void => {
          void claimsQuery.refetch();
        },
      }
    : null;

  return (
    <RelationPanel
      title="Reported by parents"
      count={claimsQuery.data ? items.length : undefined}
      description="Payments parents reported for this cycle."
      loading={claimsQuery.isLoading && !claimsQuery.data}
      error={errorState}
      isEmpty={!claimsQuery.isLoading && items.length === 0}
      empty={{ copy: 'No payments reported for this cycle yet.' }}
    >
      <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        {items.map((row) => (
          <li
            key={row.claim.id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5"
          >
            <div className="min-w-0">
              <div className="font-medium text-[color:var(--color-text)] truncate">
                {row.student.name}
              </div>
              <div className="text-xs text-[color:var(--color-text-muted)]">
                {timeAgo(row.claim.createdAt)} · {formatINR(row.payment.netAmountPaise)}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ClaimStatusBadge status={row.claim.status} />
              <Link
                to="/finance/fees/payments/$paymentId"
                params={{ paymentId: row.payment.id }}
                className="text-xs text-gold-700 underline hover:no-underline"
              >
                View payment
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </RelationPanel>
  );
}
